import { Campaign, Collab, ContentDeadlineKind, GoLiveWindowDto, deadlineKindFor, draftPhaseDone, formatPostByDate, goLiveKind, goLiveLabel, lateApprovalPostingDay, windowOf } from '@foodfluence/shared';

/** Creator-facing go-live copy (shared goLiveLabel, short dates: "on Oct 28" / "between Oct 26 and Nov 1"). */
export const goLiveLabels = (window: GoLiveWindowDto) => ({
  /** Explore badge: "Post on Oct 28" / "Post between Oct 26 and Nov 1". */
  post: `Post ${goLiveLabel(window, 'short')}`,
  /** Confirm checkbox: "I can post between Oct 26 and Nov 1". */
  confirm: `I can post ${goLiveLabel(window, 'short')}`,
});

/** What the collab's own deadline (contentDueDate) is called: drafts are due first on draft collabs. */
export const deadlineLabels = (kind: ContentDeadlineKind) => ({
  countdown: kind === 'draft' ? 'Draft due' : 'Post by',
  /** Scheduling slot card prefix. */
  slot: kind === 'draft' ? 'Draft due by' : 'Post by',
  /** Creative brief row title. */
  brief: kind === 'draft' ? 'Draft Due Date' : 'Content Due Date',
});

/** "Oct 23" for a single day, "Oct 26 – Nov 1" for a range. */
export const windowDates = (window: GoLiveWindowDto): string =>
  window.start === window.end
    ? formatPostByDate(window.start, 'short')
    : `${formatPostByDate(window.start, 'short')} – ${formatPostByDate(window.end, 'short')}`;

/** Brief go-live row: "Post on Oct 23" / "Post Oct 26 – Nov 1". */
export const goLivePostText = (window: GoLiveWindowDto): string =>
  `Post ${window.start === window.end ? 'on ' : ''}${windowDates(window)}`;

/** "Oct 17": the calendar day of `date` in `timezone`. */
const shortDay = (date: Date, timezone: string): string =>
  date.toLocaleDateString('en-US', { timeZone: timezone, month: 'short', day: 'numeric' });

export type CountdownProps = { label: string; dateText?: string; postText?: string };

/**
 * Countdown heading, the deadline date under it, and (while a go-live draft is owed) the posting
 * day(s) after it. `due` is the countdown's own getContentDueDateRaw(collab, campaign, timezone), so
 * the date shown and the timer agree; go-live posting shows the window instead.
 */
export const countdownProps = (collab: Collab, campaign: Campaign, due: Date, timezone: string): CountdownProps => {
  const dateText = shortDay(due, timezone);
  const window = windowOf(collab);
  if (!window) {
    const posting = deadlineKindFor(campaign, collab) === 'post' || draftPhaseDone(collab);
    return { label: deadlineLabels(posting ? 'post' : 'draft').countdown, dateText };
  }
  if (goLiveKind(campaign, collab) !== 'post' && !draftPhaseDone(collab)) {
    return { label: deadlineLabels('draft').countdown, dateText, postText: `Then ${goLivePostText(window).replace(/^Post/, 'post')}` };
  }
  const late = lateApprovalPostingDay(collab, window.end, timezone);
  if (late) return { label: 'Post by', dateText: formatPostByDate(late, 'short') };
  return { label: window.start === window.end ? 'Post on' : 'Post between', dateText: windowDates(window) };
};
